import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutContent } from '@/mocks/services';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9spcal8o1vn02ekujk0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('loading');

    try {
      const formData = new FormData(e.currentTarget as HTMLFormElement);
      const honeypot = formData.get('phone_alt') as string;
      if (honeypot && honeypot.trim()) {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快回复！');
        (e.target as HTMLFormElement).reset();
        return;
      }
      formData.delete('phone_alt');

      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快回复！');
        (e.target as HTMLFormElement).reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          setFormMsg('感谢您的留言！');
          (e.target as HTMLFormElement).reset();
        } else {
          setFormStatus('error');
          setFormMsg(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试');
    }
  };

  const breadcrumbItems = [{ label: '首页', to: '/' }, { label: '关于我们' }];

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <section className="mb-14">
              <div className="text-center mb-10">
                <p className="text-sm text-primary-500 font-medium mb-2">{aboutContent.tagline}</p>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  关于{aboutContent.brandName}
                </h1>
                <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                  {aboutContent.intro}
                </p>
              </div>

              <div className="relative w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Asian%20digital%20office%20with%20diverse%20team%20collaborating%20around%20holographic%20displays%2C%20warm%20coral%20and%20gold%20accent%20lighting%2C%20sleek%20minimalist%20workspace%20design%2C%20inspirational%20atmosphere&width=1400&height=700&seq=about-hero-asia&orientation=landscape"
                  alt="亚洲在线团队"
                  title="亚洲在线 - 连接亚洲精彩数字生活"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{aboutContent.mission}</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{aboutContent.vision}</p>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutContent.values.map((v, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 mb-3">
                      <span className="text-lg font-bold">{idx + 1}</span>
                    </div>
                    <h4 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                      {v.title}
                    </h4>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {aboutContent.milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className={`flex flex-col md:flex-row gap-4 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="md:w-1/2 flex md:justify-end">
                        <div className={`bg-background-100 rounded-xl p-5 border border-background-200/70 md:max-w-sm ${
                          idx % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                        }`}>
                          <span className="inline-block px-3 py-1 text-xs font-bold bg-primary-500 text-white rounded-full mb-2">
                            {m.year}
                          </span>
                          <p className="text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex items-center justify-center w-8 flex-shrink-0">
                        <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></div>
                      </div>
                      <div className="md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">联系方式</h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                        <p className="text-sm text-foreground-500">{aboutContent.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">联系电话</p>
                        <p className="text-sm text-foreground-500">{aboutContent.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">办公地址</p>
                        <p className="text-sm text-foreground-500">{aboutContent.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">工作时间</p>
                        <p className="text-sm text-foreground-500">{aboutContent.contact.workingHours}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">发送消息</h3>
                  <form onSubmit={handleContactSubmit} data-readdy-form="" className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <div className="website-alt-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                    {formMsg && (
                      <p className={`text-xs ${formStatus === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                        {formMsg}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于${aboutContent.brandName}`,
            description: aboutContent.intro,
          })}
        </script>
      </main>
      <Footer formUrl={NEWSLETTER_FORM_URL} />
    </>
  );
}